import type { Client, EntreeAppel } from '../../api/client.js';
import { saisieValide } from './modele.js';
import type {
  ConfirmationServeur,
  DonneesAdmin,
  Etude,
  EtudeSaisie,
  ParametreLegal,
  ParametreSaisie,
  Reglage,
  SessionIncarnation,
  Utilisateur,
} from './types.js';

/**
 * Entrée d'appel d'une écriture faite « en tant que » : `confirmationId` est l'identifiant reçu de la confirmation, que le
 * client d'API doit envoyer dans l'en-tête `Confirmation-Id` (RFC-L59-002). Tant que le client ne le transporte pas, le
 * serveur refuse l'écriture (CONFIRMATION_REQUIRED) et l'écran affiche ce refus : échec fermé.
 */
export type EntreeConfirmee = EntreeAppel & { confirmationId?: string };

/** Erreur de saisie détectée avant tout appel : son message est montré tel quel. */
export class ErreurSaisie extends Error {}

interface Page<T> {
  items: T[];
}

export async function chargerAdministration(client: Client): Promise<DonneesAdmin> {
  const [utilisateurs, etudes, parametres, reglages, historique] = await Promise.all([
    client.appeler<Page<Utilisateur>>('admin.utilisateurs'),
    client.appeler<Etude[]>('admin.etudes'),
    client.appeler<ParametreLegal[]>('admin.parametresLegaux'),
    client.appeler<Reglage[]>('beta.reglages'),
    client.appeler<Page<SessionIncarnation>>('impersonation.historique'),
  ]);
  return { utilisateurs: utilisateurs.items, etudes, parametres, reglages, historique: historique.items };
}

export const demarrerIncarnation = (client: Client, cibleId: string, motif: string, mode: 'read' | 'act' = 'read') =>
  client.appeler<SessionIncarnation>('impersonation.demarrer', { corps: { targetUserId: cibleId, reason: motif.trim(), mode } });

export const terminerIncarnation = (client: Client, sessionId: string) =>
  client.appeler<SessionIncarnation>('impersonation.terminer', { parametres: { sessionId } });

const canonique = (valeur: unknown): unknown =>
  Array.isArray(valeur)
    ? valeur.map(canonique)
    : valeur && typeof valeur === 'object'
      ? Object.fromEntries(
          Object.entries(valeur as Record<string, unknown>)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([k, v]) => [k, canonique(v)]),
        )
      : valeur;

/** Empreinte SHA-256 (hexadécimal) d'un corps, identique à `empreinteCorps` du cœur : clés triées par unités de code. */
export async function empreinte(charge: unknown): Promise<string> {
  const octets = new TextEncoder().encode(JSON.stringify(canonique(charge)));
  const condense = await globalThis.crypto.subtle.digest('SHA-256', octets);
  return Array.from(new Uint8Array(condense), (o) => o.toString(16).padStart(2, '0')).join('');
}

/**
 * Confirme une action avant qu'elle ne parte. `permission` est celle de la route (et non son identifiant) et `corps` le corps
 * RÉEL de l'écriture qui suivra (`undefined` pour une écriture sans corps, comptée comme `{}`), faute de quoi le serveur refuse.
 */
export async function confirmerAction(client: Client, sessionId: string, permission: string, corps: unknown) {
  return client.appeler<ConfirmationServeur>('impersonation.confirmer', {
    parametres: { sessionId },
    corps: { action: permission, payloadHash: await empreinte(corps ?? {}) },
  });
}

const avecConfirmation = (entree: EntreeAppel, confirmationId?: string): EntreeConfirmee =>
  confirmationId ? { ...entree, confirmationId } : entree;

export const creerEtude = (client: Client, saisie: EtudeSaisie, confirmationId?: string) =>
  client.appeler<Etude>('admin.etudes.creer', avecConfirmation({ corps: saisie }, confirmationId));

export const modifierEtude = (client: Client, etudeId: string, version: number, changements: Partial<EtudeSaisie>) =>
  client.appeler<Etude>('admin.etudes.modifier', { parametres: { etudeId }, corps: changements, versionLigne: version });

export const supprimerEtude = (client: Client, etudeId: string, confirmationId?: string) =>
  client.appeler<void>('admin.etudes.supprimer', avecConfirmation({ parametres: { etudeId } }, confirmationId));

/** T-R-PARAM-02 : on ajoute une valeur datée, on ne modifie jamais l'ancienne en place. */
export async function versionnerParametre(client: Client, saisie: ParametreSaisie, confirmationId?: string): Promise<ParametreLegal> {
  if (!saisieValide(saisie.key, saisie.value)) throw new ErreurSaisie('La valeur saisie ne correspond pas au paramètre.');
  return client.appeler<ParametreLegal>('admin.parametresLegaux.versionner', avecConfirmation({ corps: saisie }, confirmationId));
}
